// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { api } from '@/api/axios-instance';
import { handleAPIError } from '@/api/error-utils';
import { saveBlobResponse } from '@/file/utilities/save-blob-response';
import { useMutationWithGlobalErrorHandler } from '@/hooks/use-mutation-with-global-error-handler';
import { isAxiosError } from 'axios';

// A backend file-export request: an endpoint path, optional query params, and the
// filename to fall back to when the response omits a Content-Disposition header.
interface DownloadFileRequest {
  path: string;
  params?: URLSearchParams;
  fallbackFilename: string;
}

async function downloadFile(request: DownloadFileRequest): Promise<void> {
  try {
    const response = await api.get(request.path, {
      params: request.params,
      responseType: 'blob',
    });

    saveBlobResponse(
      response.data,
      response.headers['content-disposition'],
      request.fallbackFilename
    );
  } catch (error) {
    // Reported here rather than from onError: the global handler treats a
    // non-throwing onError as "handled locally" and would then skip the
    // user-facing message, which is the whole point of using it.
    const apiError = isAxiosError(error) ? handleAPIError(error) : undefined;
    if (!apiError?.sentToSentry) {
      Sentry.captureException(error);
    }
    throw error;
  }
}

/**
 * Downloads a backend-generated file through an authenticated request and saves it
 * under the server's Content-Disposition filename, instead of opening a new tab.
 * Exposes pending state so callers can show a spinner while the blob is fetched.
 */
export function useDownloadFile() {
  return useMutationWithGlobalErrorHandler({
    mutationFn: downloadFile,
    errorMessage: 'Could not generate that export. Please try again.',
  });
}
